export type PagedListItemState = "unloaded" | "loading" | "loaded" | "error"
export type PagedListScrollDirection = "forward" | "backward" | "none"

export interface PagedListPageInfo {
  startCursor?: string | null
  endCursor?: string | null
}

export interface PagedListFetchPageRequest {
  pageIndex: number
  /** Number of items requested from the transport. */
  pageSize: number
  /** Logical PagedList cache page size. */
  logicalPageSize: number
  /** Number of contiguous logical cache pages coalesced into this fetch. */
  pageCount: number
  /** Explicit alias for pageSize when handling coalesced requests. */
  fetchSize: number
  direction: "initial" | "forward" | "backward"
  after?: string
  before?: string
  skip: number
  includeTotalCount: boolean
  signal: AbortSignal
}

export interface PagedListFetchPageResult<T> {
  items: readonly T[]
  pageInfo?: PagedListPageInfo
  totalCount?: number
  hasNextPage?: boolean
  hasPreviousPage?: boolean
}

export interface PagedListPrefetchOptions {
  /** Pages to anticipate in the current scroll direction. */
  ahead?: number
  /** Pages to keep warm behind the current scroll direction. */
  behind?: number
}

export interface PagedListLoadAllOptions {
  /** Items per request; rounded up to whole pages. The backend may return fewer. Default: one page. */
  fetchSize?: number
  /** Most items to load. A larger result set ends the load with "too-large". */
  maxItems: number
  signal?: AbortSignal
}

/** "complete": every item is cached. "too-large": the result set holds more than maxItems. */
export type PagedListLoadAllResult = "complete" | "too-large"

export interface PagedListOptions<T> {
  fetchPage: (request: PagedListFetchPageRequest) => Promise<PagedListFetchPageResult<T>>
  pageSize?: number
  /** Request totalCount until it has been obtained. Keep false for infinite lists. */
  includeTotalCount?: boolean
  prefetch?: PagedListPrefetchOptions
  maxConcurrentRequests?: number
}

interface LoadedPage<T> {
  index: number
  items: readonly T[]
  pageInfo: PagedListPageInfo
  hasNextPage: boolean
  hasPreviousPage: boolean
  loadedAt: number
}

interface ViewportRange {
  start: number
  end: number
  direction: PagedListScrollDirection
}

interface Deferred {
  promise: Promise<void>
  resolve: () => void
  reject: (reason?: unknown) => void
}

interface QueuedPage {
  pageIndex: number
  priority: number
  refresh: boolean
  /** Requested by loadAll only; viewport moves do not cancel it. */
  background: boolean
  deferred: Deferred
}

interface InFlightPage {
  controller: AbortController
  promise: Promise<void>
  priority: number
  background: boolean
  pageIndexes: readonly number[]
}

interface MissingPage {
  pageIndex: number
  /** The page is cached but stale or short, and must be fetched again. */
  refresh: boolean
}

const DEFAULT_PAGE_SIZE = 100
const DEFAULT_PREFETCH_AHEAD = 2
const DEFAULT_PREFETCH_BEHIND = 1
const DEFAULT_MAX_CONCURRENT_REQUESTS = 4
/** Below every viewport and prefetch priority. */
const BACKGROUND_PRIORITY = Number.MAX_SAFE_INTEGER
/** loadAll rounds that pass without loading anything before it gives up. */
const MAX_IDLE_LOAD_ALL_ROUNDS = 3

function createDeferred(): Deferred {
  let resolve!: () => void
  let reject!: (reason?: unknown) => void
  const promise = new Promise<void>((res, rej) => {
    resolve = res
    reject = rej
  })
  return { promise, resolve, reject }
}

function createAbortError() {
  const error = new Error("PagedList request was aborted.")
  error.name = "AbortError"
  return error
}

function isAbortError(error: unknown) {
  return error instanceof Error && error.name === "AbortError"
}

/** Resolves once every request settled, cancelled ones included; rejects at the first one that failed. */
function settleUnlessFailed(requests: readonly Promise<void>[]) {
  return new Promise<void>((resolve, reject) => {
    let remaining = requests.length
    const settle = () => {
      if (--remaining === 0) resolve()
    }
    if (remaining === 0) resolve()
    for (const request of requests) {
      request.then(settle, (error: unknown) => (isAbortError(error) ? settle() : reject(error)))
    }
  })
}

/**
 * Sparse, observable page cache intended for virtualized UI controls.
 *
 * Reading an item never starts or waits for I/O. Consumers call ensureRange()
 * with their visible range; missing pages load in the background and subscribers
 * are notified as state changes.
 */
export class PagedList<T> {
  readonly pageSize: number

  private readonly fetchPage: PagedListOptions<T>["fetchPage"]
  private readonly includeTotalCount: boolean
  private readonly prefetchAhead: number
  private readonly prefetchBehind: number
  private readonly maxConcurrentRequests: number

  private readonly pages = new Map<number, LoadedPage<T>>()
  private readonly stalePages = new Set<number>()
  private readonly errors = new Map<number, unknown>()
  private readonly listeners = new Set<() => void>()
  private readonly dataListeners = new Set<() => void>()
  private readonly queued = new Map<number, QueuedPage>()
  private readonly inFlight = new Map<number, InFlightPage>()

  private totalCountValue?: number
  private revisionValue = 0
  private dataRevisionValue = 0
  private lastViewport?: ViewportRange
  private countRequestSignal: AbortSignal | undefined
  private countRefreshNeeded = false
  private unsettledRequestCount = 0
  private backgroundRequestCount = 0
  private backgroundBatchPages = 1
  /** Changes when invalidate() starts a new result set, which ends a running loadAll. */
  private generation = 0
  private disposed = false

  constructor(options: PagedListOptions<T>) {
    this.fetchPage = options.fetchPage
    this.pageSize = Math.max(1, Math.trunc(options.pageSize ?? DEFAULT_PAGE_SIZE))
    this.includeTotalCount = options.includeTotalCount ?? false
    this.prefetchAhead = Math.max(0, Math.trunc(options.prefetch?.ahead ?? DEFAULT_PREFETCH_AHEAD))
    this.prefetchBehind = Math.max(0, Math.trunc(options.prefetch?.behind ?? DEFAULT_PREFETCH_BEHIND))
    this.maxConcurrentRequests = Math.max(
      1,
      Math.trunc(options.maxConcurrentRequests ?? DEFAULT_MAX_CONCURRENT_REQUESTS)
    )
  }

  get totalCount() {
    return this.totalCountValue
  }

  /** Actual number of item objects currently held in the page cache. */
  get loadedCount() {
    let count = 0
    for (const page of this.pages.values()) count += page.items.length
    return count
  }

  /**
   * Row count a virtualizer can currently expose.
   * Exact when totalCount is known; otherwise provides one unloaded page of
   * runway after the contiguous loaded data while more data may exist.
   */
  get itemCount() {
    if (this.totalCountValue !== undefined) return this.totalCountValue
    const contiguousCount = this.getContiguousLoadedCount()
    return contiguousCount + (this.hasNextPage ? this.pageSize : 0)
  }

  get isLoading() {
    return this.queued.size > 0 || this.inFlight.size > 0
  }

  get hasNextPage() {
    if (this.totalCountValue !== undefined) {
      return this.getContiguousLoadedCount() < this.totalCountValue
    }

    const lastContiguousPage = this.getLastContiguousPage()
    return lastContiguousPage?.hasNextPage ?? true
  }

  get hasPreviousPage() {
    if (this.pages.size === 0) return false
    const lowestPage = Math.min(...this.pages.keys())
    return lowestPage > 0 || (this.pages.get(lowestPage)?.hasPreviousPage ?? false)
  }

  /** Stable primitive snapshot suitable for React useSyncExternalStore. */
  get revision() {
    return this.revisionValue
  }

  /** Revision that changes only when cached row data or itemCount changes. */
  get dataRevision() {
    return this.dataRevisionValue
  }

  get(index: number): T | undefined {
    if (!Number.isInteger(index) || index < 0) return undefined
    const page = this.pages.get(Math.floor(index / this.pageSize))
    return page?.items[index % this.pageSize]
  }

  /** Enumerates cached records only, without scanning the logical collection. */
  *getLoadedEntries(): Iterable<readonly [number, T]> {
    for (const page of this.pages.values()) {
      for (let offset = 0; offset < page.items.length; offset++) {
        yield [page.index * this.pageSize + offset, page.items[offset]!]
      }
    }
  }

  getItemState(index: number): PagedListItemState {
    if (!Number.isInteger(index) || index < 0) return "unloaded"
    const pageIndex = Math.floor(index / this.pageSize)
    const page = this.pages.get(pageIndex)
    if (page?.items[index % this.pageSize] !== undefined) return "loaded"
    if (this.errors.has(pageIndex)) return "error"
    if (this.queued.has(pageIndex) || this.inFlight.has(pageIndex)) return "loading"
    return "unloaded"
  }

  getPageError(pageIndex: number) {
    return this.errors.get(pageIndex)
  }

  /** Whether any page, also outside the last viewport, failed with an error matching the predicate. */
  hasPageError(predicate: (error: unknown) => boolean = () => true) {
    for (const error of this.errors.values()) if (predicate(error)) return true
    return false
  }

  hasLoadedPage(pageIndex: number) {
    return this.pages.has(pageIndex)
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /** Subscribe only to cached row-data/item-count changes. */
  subscribeData(listener: () => void) {
    this.dataListeners.add(listener)
    return () => this.dataListeners.delete(listener)
  }

  /**
   * Fire-and-forget visible-range request. Missing visible pages get highest
   * priority; adjacent pages are prefetched in the background.
   */
  ensureRange(start: number, end: number, direction?: PagedListScrollDirection) {
    void this.loadRange(start, end, direction).catch(() => {
      // Errors are retained per page and exposed through getItemState/getPageError.
    })
  }

  /** Loads all pages intersecting the range and resolves when those visible pages finish. */
  async loadRange(start: number, end: number, direction?: PagedListScrollDirection) {
    if (this.disposed) return
    const range = this.normalizeRange(start, end, direction)
    if (range == null) return

    this.lastViewport = range
    const startPage = Math.floor(range.start / this.pageSize)
    const endPage = Math.floor(range.end / this.pageSize)
    const desiredPages = this.buildDesiredPagePriorities(startPage, endPage, range.direction)

    // A virtualized viewport can move much faster than the network. Requests
    // outside the new visible+prefetch window no longer provide value, so abort
    // them. They still consume a concurrency slot until fetchPage actually
    // settles, preventing rapid scrolling from oversubscribing the transport.
    this.cancelObsoleteRequests(desiredPages)

    const visiblePromises: Promise<void>[] = []
    for (let pageIndex = startPage; pageIndex <= endPage; pageIndex++) {
      if (this.errors.has(pageIndex)) continue
      visiblePromises.push(this.schedulePage(pageIndex, 0, false))
    }

    for (const [pageIndex, priority] of desiredPages) {
      if (pageIndex >= startPage && pageIndex <= endPage) continue
      if (!this.errors.has(pageIndex)) void this.schedulePage(pageIndex, priority, false).catch(() => {})
    }
    this.pumpQueue()
    await Promise.all(visiblePromises)
  }

  /** Explicit low-priority prefetch without changing the active viewport. */
  prefetchRange(start: number, end: number) {
    const normalized = this.normalizeIndexes(start, end)
    if (normalized == null) return
    const startPage = Math.floor(normalized.start / this.pageSize)
    const endPage = Math.floor(normalized.end / this.pageSize)
    for (let pageIndex = startPage; pageIndex <= endPage; pageIndex++) {
      if (!this.errors.has(pageIndex)) void this.schedulePage(pageIndex, 1, false).catch(() => {})
    }
    this.pumpQueue()
  }

  /** Whether every item of the result set is cached, including stale pages awaiting refresh. */
  get isComplete() {
    if (this.totalCountValue !== undefined) {
      for (let pageIndex = 0; pageIndex * this.pageSize < this.totalCountValue; pageIndex++) {
        if (!this.isPageFull(pageIndex)) return false
      }
      return true
    }
    for (let pageIndex = 0; ; pageIndex++) {
      const page = this.pages.get(pageIndex)
      if (page == null) return false
      if (!page.hasNextPage) return true
      if (page.items.length < this.pageSize) return false
    }
  }

  /**
   * Loads every page of the result set in a background lane behind the viewport, and refreshes stale ones. Viewport
   * moves do not cancel it; a new result set (invalidate) or the signal does, with an AbortError. A known count above
   * maxItems ends it before loading. Without a count it loads forward one request at a time, since it cannot know
   * where the result ends. A failed page rejects with that page's error; clearErrors() lets a later call resume.
   */
  async loadAll(options: PagedListLoadAllOptions): Promise<PagedListLoadAllResult> {
    const { signal } = options
    const maxItems = Math.max(0, Math.trunc(options.maxItems))
    const generation = this.generation
    this.backgroundBatchPages = Math.max(1, Math.ceil((options.fetchSize ?? this.pageSize) / this.pageSize))
    const assertCurrent = () => {
      if (signal?.aborted || this.disposed || generation !== this.generation) throw createAbortError()
    }
    const cancel = () => this.cancelBackgroundRequests()
    signal?.addEventListener("abort", cancel, { once: true })
    try {
      let idleRounds = 0
      while (true) {
        assertCurrent()
        if (this.totalCountValue !== undefined && this.totalCountValue > maxItems) return "too-large"
        const missing = this.getMissingPages()
        for (const { pageIndex } of missing) {
          if (this.errors.has(pageIndex)) throw this.errors.get(pageIndex)
        }
        if (missing.length === 0) return "complete"
        if (this.totalCountValue === undefined && this.loadedCount > maxItems) return "too-large"

        const progress = this.getLoadProgress()
        const requests = missing.map(({ pageIndex, refresh }) =>
          this.schedulePage(pageIndex, BACKGROUND_PRIORITY, false, refresh, true)
        )
        this.pumpQueue()
        await settleUnlessFailed(requests)
        assertCurrent()
        // A backend that keeps answering short of its own count would otherwise be asked forever.
        idleRounds = this.getLoadProgress() === progress ? idleRounds + 1 : 0
        if (idleRounds >= MAX_IDLE_LOAD_ALL_ROUNDS) throw new Error("PagedList could not load every page.")
      }
    } finally {
      signal?.removeEventListener("abort", cancel)
    }
  }

  /** Forget page errors, so that later loads, such as a resumed loadAll, request those pages again. */
  clearErrors() {
    if (this.errors.size === 0) return
    this.errors.clear()
    this.emitActivity()
  }

  async retryRange(start: number, end: number) {
    const normalized = this.normalizeIndexes(start, end)
    if (normalized == null) return
    const startPage = Math.floor(normalized.start / this.pageSize)
    const endPage = Math.floor(normalized.end / this.pageSize)
    for (let pageIndex = startPage; pageIndex <= endPage; pageIndex++) this.errors.delete(pageIndex)
    this.emitActivity()
    await this.loadRange(start, end)
  }

  /**
   * Re-fetch only loaded pages containing matching items. Existing rows remain
   * visible until the refreshed page arrives.
   */
  async refreshWhere(predicate: (item: T) => boolean) {
    if (this.disposed) return 0
    const pageIndexes = [...this.pages].filter(([, page]) => page.items.some(predicate)).map(([pageIndex]) => pageIndex)
    if (pageIndexes.length === 0) return 0

    const interrupted = this.cancelRequestsForPages(pageIndexes)
    const refreshPageIndexes = [...new Set([...pageIndexes, ...interrupted])]
    const requests = refreshPageIndexes.map((pageIndex) => this.schedulePage(pageIndex, 0, false, true))
    this.pumpQueue()
    await Promise.all(requests)
    return pageIndexes.length
  }

  /** Refresh viewport + prefetch; retain other stale pages until requested again. */
  async refresh() {
    if (this.disposed) return
    this.abortRequests()
    this.errors.clear()
    this.countRefreshNeeded = true
    // Structural changes invalidate old cursors. Use absolute offsets until
    // refreshed anchors arrive, while leaving cached rows visible.
    for (const [index, page] of this.pages) {
      this.pages.set(index, { ...page, pageInfo: {} })
      this.stalePages.add(index)
    }
    const viewport = this.lastViewport
    const pages = this.buildDesiredPagePriorities(
      viewport ? Math.floor(viewport.start / this.pageSize) : 0,
      viewport ? Math.floor(viewport.end / this.pageSize) : 0,
      viewport?.direction ?? "none"
    )
    // A known-empty collection still needs a request to discover inserted rows.
    if (pages.size === 0) pages.set(0, 0)
    const requests = [...pages].map(([index, priority]) => this.schedulePage(index, priority, false, true))
    this.pumpQueue()
    await Promise.all(requests)
  }

  /** Replace already-cached items by stable key without changing collection structure. */
  replaceItems(items: readonly T[], getKey: (item: T) => string | number) {
    if (this.disposed || items.length === 0) return 0
    const replacements = new Map(items.map((item) => [getKey(item), item] as const))
    let replaced = 0
    for (const [pageIndex, page] of this.pages) {
      let changed = false
      const nextItems = page.items.map((item) => {
        const replacement = replacements.get(getKey(item))
        if (replacement === undefined) return item
        changed = true
        replaced++
        return replacement
      })
      if (changed) this.pages.set(pageIndex, { ...page, items: nextItems, loadedAt: Date.now() })
    }
    if (replaced > 0) this.emitData()
    return replaced
  }

  /** Clear cache and requests without immediately fetching anything. */
  invalidate() {
    this.generation++
    this.abortRequests()
    this.pages.clear()
    this.stalePages.clear()
    this.errors.clear()
    this.totalCountValue = undefined
    this.countRequestSignal = undefined
    this.countRefreshNeeded = false
    this.emitData()
  }

  /** Clear the cache and reload the last visible range, if one exists. */
  async reload() {
    const viewport = this.lastViewport
    this.invalidate()
    if (viewport != null) await this.loadRange(viewport.start, viewport.end, viewport.direction)
  }

  /**
   * Abort queued and in-flight requests without disposing the list or clearing
   * its cached pages. A later ensureRange/loadRange call can resume loading.
   * Useful when a UI subtree is temporarily inactive but its document state is
   * retained in memory.
   */
  cancelPendingRequests() {
    if (this.disposed || !this.isLoading) return
    this.abortRequests()
    this.emitActivity()
  }

  dispose() {
    if (this.disposed) return
    this.disposed = true
    this.abortRequests()
    this.listeners.clear()
    this.dataListeners.clear()
  }

  private normalizeRange(start: number, end: number, direction?: PagedListScrollDirection) {
    const normalized = this.normalizeIndexes(start, end)
    if (normalized == null) return undefined

    let resolvedDirection = direction ?? "none"
    if (direction == null && this.lastViewport != null) {
      const previousMidpoint = (this.lastViewport.start + this.lastViewport.end) / 2
      const midpoint = (normalized.start + normalized.end) / 2
      resolvedDirection =
        midpoint > previousMidpoint ? "forward" : midpoint < previousMidpoint ? "backward" : this.lastViewport.direction
    }

    return { ...normalized, direction: resolvedDirection }
  }

  private normalizeIndexes(start: number, end: number) {
    if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined
    let normalizedStart = Math.max(0, Math.trunc(Math.min(start, end)))
    let normalizedEnd = Math.max(0, Math.trunc(Math.max(start, end)))

    if (this.totalCountValue !== undefined) {
      if (this.totalCountValue === 0 || normalizedStart >= this.totalCountValue) return undefined
      normalizedEnd = Math.min(normalizedEnd, this.totalCountValue - 1)
      normalizedStart = Math.min(normalizedStart, normalizedEnd)
    }

    return { start: normalizedStart, end: normalizedEnd }
  }

  private buildDesiredPagePriorities(
    visibleStartPage: number,
    visibleEndPage: number,
    direction: PagedListScrollDirection
  ) {
    const pages = new Map<number, number>()

    const addWindow = (startPage: number, endPage: number, priority: number) => {
      for (let pageIndex = Math.max(0, startPage); pageIndex <= endPage; pageIndex++) {
        if (!this.isPageInKnownRange(pageIndex)) continue
        const previousPriority = pages.get(pageIndex)
        pages.set(pageIndex, previousPriority === undefined ? priority : Math.min(previousPriority, priority))
      }
    }

    addWindow(visibleStartPage, visibleEndPage, 0)

    if (direction === "backward") {
      addWindow(visibleStartPage - this.prefetchAhead, visibleStartPage - 1, 1)
      addWindow(visibleEndPage + 1, visibleEndPage + this.prefetchBehind, 2)
      return pages
    }

    if (direction === "forward") {
      addWindow(visibleEndPage + 1, visibleEndPage + this.prefetchAhead, 1)
      addWindow(visibleStartPage - this.prefetchBehind, visibleStartPage - 1, 2)
      return pages
    }

    addWindow(visibleEndPage + 1, visibleEndPage + this.prefetchAhead, 1)
    addWindow(visibleStartPage - this.prefetchBehind, visibleStartPage - 1, 1)
    return pages
  }

  private cancelObsoleteRequests(desiredPages: ReadonlyMap<number, number>) {
    const abortError = createAbortError()
    let changed = false

    for (const [pageIndex, queued] of this.queued) {
      if (queued.background || desiredPages.has(pageIndex)) continue
      this.queued.delete(pageIndex)
      queued.deferred.reject(abortError)
      changed = true
    }

    const visitedRequests = new Set<InFlightPage>()
    for (const request of this.inFlight.values()) {
      if (visitedRequests.has(request)) continue
      visitedRequests.add(request)
      if (request.background || request.pageIndexes.some((pageIndex) => desiredPages.has(pageIndex))) continue

      // Remove first so newly relevant pages can be scheduled immediately.
      // The old request's finally block is guarded by controller identity and
      // cannot delete replacement requests.
      for (const pageIndex of request.pageIndexes) {
        if (this.inFlight.get(pageIndex)?.controller === request.controller) this.inFlight.delete(pageIndex)
      }
      request.controller.abort()
      changed = true
    }

    if (changed) {
      this.emitActivity()
    }
  }

  private schedulePage(
    pageIndex: number,
    priority: number,
    pump = true,
    refresh = false,
    background = false
  ): Promise<void> {
    refresh ||= this.stalePages.has(pageIndex)
    if (
      this.disposed ||
      pageIndex < 0 ||
      (!refresh && this.pages.has(pageIndex)) ||
      (!(refresh && pageIndex === 0) && !this.isPageInKnownRange(pageIndex))
    ) {
      return Promise.resolve()
    }

    const running = this.inFlight.get(pageIndex)
    if (running != null) return running.promise

    const existing = this.queued.get(pageIndex)
    if (existing != null) {
      existing.priority = Math.min(existing.priority, priority)
      existing.refresh ||= refresh
      // A page the viewport also wants follows the viewport from now on.
      existing.background &&= background
      return existing.deferred.promise
    }

    const deferred = createDeferred()
    this.queued.set(pageIndex, { pageIndex, priority, refresh, background, deferred })
    this.emitActivity()
    if (pump) this.pumpQueue()
    return deferred.promise
  }

  private pumpQueue() {
    while (!this.disposed && this.unsettledRequestCount < this.maxConcurrentRequests && this.queued.size > 0) {
      const nextBatch = this.pickNextQueuedBatch()
      if (nextBatch.length === 0) return
      const background = nextBatch[0]!.background
      // The background lane leaves one request slot to the viewport.
      if (background && this.backgroundRequestCount >= Math.max(1, this.maxConcurrentRequests - 1)) return
      for (const queued of nextBatch) this.queued.delete(queued.pageIndex)

      const pending = nextBatch.filter((queued) => {
        if (
          (!queued.refresh && this.pages.has(queued.pageIndex)) ||
          (!(queued.refresh && queued.pageIndex === 0) && !this.isPageInKnownRange(queued.pageIndex))
        ) {
          queued.deferred.resolve()
          return false
        }
        return true
      })
      if (pending.length === 0) continue

      const controller = new AbortController()
      const pageIndexes = pending.map((queued) => queued.pageIndex)
      const priority = Math.min(...pending.map((queued) => queued.priority))
      this.unsettledRequestCount++
      if (background) this.backgroundRequestCount++
      const promise = this.executePageRequest(pageIndexes, controller.signal)
        .then(() => pending.forEach((queued) => queued.deferred.resolve()))
        // An aborted transport may fail with its own error; callers only need to know the request was cancelled.
        .catch((error) => {
          const reason = controller.signal.aborted ? createAbortError() : error
          pending.forEach((queued) => queued.deferred.reject(reason))
        })
        .finally(() => {
          this.unsettledRequestCount--
          if (background) this.backgroundRequestCount--
          for (const pageIndex of pageIndexes) {
            if (this.inFlight.get(pageIndex)?.controller === controller) this.inFlight.delete(pageIndex)
          }
          this.emitActivity()
          this.pumpQueue()
        })

      const inFlight = { controller, promise, priority, background, pageIndexes }
      for (const pageIndex of pageIndexes) this.inFlight.set(pageIndex, inFlight)
      this.emitActivity()
    }
  }

  private pickNextQueuedBatch() {
    const selected = this.pickNextQueuedPage()
    if (selected == null) return []

    const batch = [selected]
    if (selected.background) {
      // The lane picks its lowest page, so it only extends forward, up to its fetch size.
      for (let pageIndex = selected.pageIndex + 1; batch.length < this.backgroundBatchPages; pageIndex++) {
        const candidate = this.queued.get(pageIndex)
        if (candidate == null || !candidate.background || candidate.refresh !== selected.refresh) break
        batch.push(candidate)
      }
      return batch
    }
    let pageIndex = selected.pageIndex - 1
    while (pageIndex >= 0) {
      const candidate = this.queued.get(pageIndex)
      if (candidate == null || candidate.priority !== selected.priority || candidate.refresh !== selected.refresh) break
      batch.unshift(candidate)
      pageIndex--
    }

    pageIndex = selected.pageIndex + 1
    while (true) {
      const candidate = this.queued.get(pageIndex)
      if (candidate == null || candidate.priority !== selected.priority || candidate.refresh !== selected.refresh) break
      batch.push(candidate)
      pageIndex++
    }
    return batch
  }

  private pickNextQueuedPage() {
    const viewportCenterPage =
      this.lastViewport == null ? 0 : (this.lastViewport.start + this.lastViewport.end) / 2 / this.pageSize

    let selected: QueuedPage | undefined
    for (const candidate of this.queued.values()) {
      if (selected == null) {
        selected = candidate
        continue
      }

      if (candidate.priority < selected.priority) {
        selected = candidate
        continue
      }

      if (candidate.priority !== selected.priority) continue
      if (
        candidate.background
          ? candidate.pageIndex < selected.pageIndex
          : Math.abs(candidate.pageIndex - viewportCenterPage) < Math.abs(selected.pageIndex - viewportCenterPage)
      ) {
        selected = candidate
      }
    }
    return selected
  }

  private async executePageRequest(pageIndexes: readonly number[], signal: AbortSignal) {
    const pageIndex = pageIndexes[0]
    if (pageIndex === undefined) return
    const request = this.createFetchRequest(pageIndex, pageIndexes.length, signal)
    if (request.includeTotalCount) this.countRequestSignal = signal

    try {
      const result = await this.fetchPage(request)
      if (signal.aborted || this.disposed) throw createAbortError()

      const items = Array.from(result.items)
      if (
        request.includeTotalCount &&
        result.totalCount !== undefined &&
        Number.isFinite(result.totalCount) &&
        result.totalCount >= 0
      ) {
        this.totalCountValue = Math.trunc(result.totalCount)
        this.countRefreshNeeded = false
      }

      // Preserve an empty terminal page as a loaded page so unknown-length
      // lists can discover their end instead of repeatedly requesting it.
      const loadedPageCount = Math.min(pageIndexes.length, Math.max(1, Math.ceil(items.length / this.pageSize)))
      const loadedAt = Date.now()
      for (const index of pageIndexes.slice(loadedPageCount)) {
        this.pages.delete(index)
        this.stalePages.delete(index)
      }
      for (let offset = 0; offset < loadedPageCount; offset++) {
        const currentPageIndex = pageIndexes[offset]
        if (currentPageIndex === undefined) continue
        const pageItems = items.slice(offset * this.pageSize, (offset + 1) * this.pageSize)
        const absoluteEnd = currentPageIndex * this.pageSize + pageItems.length
        const isFirstLoadedPage = offset === 0
        const isLastLoadedPage = offset === loadedPageCount - 1
        const hasNextPage =
          this.totalCountValue !== undefined
            ? absoluteEnd < this.totalCountValue
            : isLastLoadedPage
              ? (result.hasNextPage ?? items.length === request.fetchSize)
              : true
        const hasPreviousPage = isFirstLoadedPage ? (result.hasPreviousPage ?? currentPageIndex > 0) : true

        this.pages.set(currentPageIndex, {
          index: currentPageIndex,
          items: pageItems,
          pageInfo: {
            startCursor: isFirstLoadedPage ? result.pageInfo?.startCursor : undefined,
            endCursor: isLastLoadedPage ? result.pageInfo?.endCursor : undefined,
          },
          hasNextPage,
          hasPreviousPage,
          loadedAt,
        })
        this.errors.delete(currentPageIndex)
        this.stalePages.delete(currentPageIndex)
      }
      if (this.totalCountValue !== undefined) {
        for (const [index, page] of this.pages) {
          const remaining = Math.max(0, this.totalCountValue - index * this.pageSize)
          if (remaining === 0 && index !== 0) {
            this.pages.delete(index)
            this.stalePages.delete(index)
          } else if (remaining < page.items.length)
            this.pages.set(index, { ...page, items: page.items.slice(0, remaining), hasNextPage: false })
        }
      }
      this.emitData()
    } catch (error) {
      if (!isAbortError(error) && !signal.aborted) {
        for (const currentPageIndex of pageIndexes) this.errors.set(currentPageIndex, error)
        this.emitActivity()
      }
      throw error
    } finally {
      if (this.countRequestSignal === signal) this.countRequestSignal = undefined
    }
  }

  private createFetchRequest(pageIndex: number, pageCount: number, signal: AbortSignal): PagedListFetchPageRequest {
    const anchor = this.getClosestLoadedPage(pageIndex)
    const includeTotalCount =
      this.includeTotalCount &&
      (this.totalCountValue === undefined || this.countRefreshNeeded) &&
      (!this.countRequestSignal || this.countRequestSignal.aborted)
    const fetchSize = this.pageSize * pageCount

    if (anchor == null) {
      return {
        pageIndex,
        pageSize: fetchSize,
        logicalPageSize: this.pageSize,
        pageCount,
        fetchSize,
        direction: "initial",
        skip: pageIndex * this.pageSize,
        includeTotalCount,
        signal,
      }
    }

    if (pageIndex > anchor.index && anchor.pageInfo.endCursor) {
      return {
        pageIndex,
        pageSize: fetchSize,
        logicalPageSize: this.pageSize,
        pageCount,
        fetchSize,
        direction: "forward",
        after: anchor.pageInfo.endCursor,
        skip: (pageIndex - anchor.index - 1) * this.pageSize,
        includeTotalCount,
        signal,
      }
    }

    if (pageIndex < anchor.index && anchor.pageInfo.startCursor) {
      return {
        pageIndex,
        pageSize: fetchSize,
        logicalPageSize: this.pageSize,
        pageCount,
        fetchSize,
        direction: "backward",
        before: anchor.pageInfo.startCursor,
        skip: Math.max(0, anchor.index - pageIndex - pageCount) * this.pageSize,
        includeTotalCount,
        signal,
      }
    }

    // Cursor unavailable: Florisoft's connection handler supports offsetting from
    // the beginning, so arbitrary virtualized jumps still remain possible.
    return {
      pageIndex,
      pageSize: fetchSize,
      logicalPageSize: this.pageSize,
      pageCount,
      fetchSize,
      direction: "initial",
      skip: pageIndex * this.pageSize,
      includeTotalCount,
      signal,
    }
  }

  private getClosestLoadedPage(targetPageIndex: number) {
    let closest: LoadedPage<T> | undefined
    for (const page of this.pages.values()) {
      if (closest == null || Math.abs(page.index - targetPageIndex) < Math.abs(closest.index - targetPageIndex)) {
        closest = page
      }
    }
    return closest
  }

  private getLastContiguousPage() {
    let pageIndex = 0
    let last: LoadedPage<T> | undefined
    while (this.pages.has(pageIndex)) {
      last = this.pages.get(pageIndex)
      if (last == null || last.items.length < this.pageSize) break
      pageIndex++
    }
    return last
  }

  private getContiguousLoadedCount() {
    let count = 0
    let pageIndex = 0
    while (this.pages.has(pageIndex)) {
      const page = this.pages.get(pageIndex)
      if (page == null) break
      count += page.items.length
      if (page.items.length < this.pageSize) break
      pageIndex++
    }
    return count
  }

  /** Whether the page holds every item it can hold: a full page, or the rest of a known count. */
  private isPageFull(pageIndex: number) {
    const page = this.pages.get(pageIndex)
    if (page == null) return false
    const expected =
      this.totalCountValue === undefined
        ? this.pageSize
        : Math.min(this.pageSize, Math.max(0, this.totalCountValue - pageIndex * this.pageSize))
    return page.items.length >= expected
  }

  /** Pages loadAll still needs: all of them with a known count, otherwise the next request's worth. */
  private getMissingPages(): MissingPage[] {
    const missing: MissingPage[] = []
    if (this.totalCountValue !== undefined) {
      for (let pageIndex = 0; pageIndex * this.pageSize < this.totalCountValue; pageIndex++) {
        if (!this.pages.has(pageIndex)) missing.push({ pageIndex, refresh: false })
        else if (this.stalePages.has(pageIndex) || !this.isPageFull(pageIndex))
          missing.push({ pageIndex, refresh: true })
      }
      return missing
    }
    for (let pageIndex = 0; ; pageIndex++) {
      const page = this.pages.get(pageIndex)
      if (page != null && (!page.hasNextPage || page.items.length >= this.pageSize)) {
        if (this.stalePages.has(pageIndex)) missing.push({ pageIndex, refresh: true })
        if (!page.hasNextPage) return missing
        continue
      }
      // The first missing or short page: one request from here; its response tells how far the result goes. A list that
      // asks for a count first learns it from the first page, so a result above the limit costs no more than that page.
      const batchPages = this.includeTotalCount && !this.pages.has(0) ? 1 : this.backgroundBatchPages
      for (let next = pageIndex; next < pageIndex + batchPages; next++) {
        missing.push({ pageIndex: next, refresh: this.pages.has(next) })
      }
      return missing
    }
  }

  private getLoadProgress() {
    return `${this.loadedCount}:${this.stalePages.size}:${this.totalCountValue}`
  }

  private cancelBackgroundRequests() {
    const abortError = createAbortError()
    let changed = false
    for (const [pageIndex, queued] of this.queued) {
      if (!queued.background) continue
      this.queued.delete(pageIndex)
      queued.deferred.reject(abortError)
      changed = true
    }
    for (const request of new Set(this.inFlight.values())) {
      if (!request.background) continue
      for (const pageIndex of request.pageIndexes) {
        if (this.inFlight.get(pageIndex)?.controller === request.controller) this.inFlight.delete(pageIndex)
      }
      request.controller.abort()
      changed = true
    }
    if (changed) this.emitActivity()
  }

  private isPageInKnownRange(pageIndex: number) {
    if (this.totalCountValue === undefined) return true
    return pageIndex * this.pageSize < this.totalCountValue
  }

  private abortRequests() {
    const abortError = createAbortError()
    const requests = new Set(this.inFlight.values())
    for (const request of requests) request.controller.abort()
    for (const queued of this.queued.values()) queued.deferred.reject(abortError)
    this.inFlight.clear()
    this.queued.clear()
  }

  private cancelRequestsForPages(pageIndexes: readonly number[]) {
    const targets = new Set(pageIndexes)
    const interruptedRefreshPages = new Set<number>()
    const abortError = createAbortError()
    let changed = false
    for (const [pageIndex, queued] of this.queued) {
      if (!targets.has(pageIndex)) continue
      this.queued.delete(pageIndex)
      queued.deferred.reject(abortError)
      changed = true
    }

    const requests = new Set(this.inFlight.values())
    for (const request of requests) {
      if (!request.pageIndexes.some((pageIndex) => targets.has(pageIndex))) continue
      for (const pageIndex of request.pageIndexes) {
        if (!targets.has(pageIndex) && this.pages.has(pageIndex)) {
          interruptedRefreshPages.add(pageIndex)
        }
        if (this.inFlight.get(pageIndex)?.controller === request.controller) this.inFlight.delete(pageIndex)
      }
      request.controller.abort()
      changed = true
    }
    if (changed) this.emitActivity()
    return interruptedRefreshPages
  }

  private emitActivity() {
    this.revisionValue++
    for (const listener of this.listeners) listener()
  }

  private emitData() {
    this.revisionValue++
    this.dataRevisionValue++
    for (const listener of this.listeners) listener()
    for (const listener of this.dataListeners) listener()
  }
}

export function createPagedList<T>(options: PagedListOptions<T>) {
  return new PagedList(options)
}
